/**
 * ZITELOU - Configuration Page
 * Gestion des administrateurs, logs et paramètres
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import {
  Card,
  Button,
  Table,
  Badge,
  Input,
    Select,
    ConfirmModal,
} from '../components/Common';
import { usePagination, useFilters, useApi } from '../hooks';
import api from '../services/api';
import { formatDate, exportToCSV } from '../utils';
import { toast } from 'react-toastify';

const AdminFormModal = ({ isOpen, onClose, onSubmit, admin = null }) => {
    const [formData, setFormData] = useState({
        firstname: admin?.firstname || '',
        lastname: admin?.lastname || '',
        email: admin?.email || '',
        password: '',
        role: admin?.role || 'admin',
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <h2 className="text-xl font-bold mb-4">
                    {admin ? 'Modifier' : 'Ajouter'} un administrateur
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <Input
                        label="Prénom"
                        value={formData.firstname}
                        onChange={(e) => setFormData({...formData, firstname: e.target.value})}
                        required
                    />
                    <Input
                        label="Nom"
                        value={formData.lastname}
                        onChange={(e) => setFormData({...formData, lastname: e.target.value})}
                        required
                    />
                    <Input
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                    />
                    <Input
                        label="Mot de passe"
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        required={!admin}
                        helperText={admin ? "Laisser vide pour conserver l'ancien" : ""}
                    />
                    <Select
                        label="Rôle"
                        value={formData.role}
                        onChange={(e) => setFormData({...formData, role: e.target.value})}
                        options={[
                            { value: 'super_admin', label: 'Super Admin' },
                            { value: 'admin', label: 'Admin' },
                            { value: 'support', label: 'Support' },
                        ]}
                    />
                    <div className="flex justify-end gap-3 mt-6">
                        <Button variant="outline" onClick={onClose}>
                            Annuler
                        </Button>
                        <Button type="submit" variant="primary">
                            {admin ? 'Modifier' : 'Créer'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const Config = () => {
  const [activeTab, setActiveTab] = useState('admins');
  const [currentUser, setCurrentUser] = useState(null);
  const { loading, execute } = useApi();
  const [admins, setAdmins] = useState([]);
  const [logs, setLogs] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

    const fetchData = useCallback(async () => {
        await execute(async () => {
            // Get the current user to check permissions
            const user = JSON.parse(localStorage.getItem('admin_user') || '{}');

            // Always load logs since all admins can see them
            const logsData = await api.getSystemLogs();
            setLogs(logsData.data);

            // Only load admins data if user is super admin
            if (user.role === 'super_admin') {
                try {
                    const adminsData = await api.getAdmins();
                    setAdmins(adminsData);
                } catch (error) {
                    console.error('Error loading admins:', error);
                    toast.error('Impossible de charger les administrateurs');
                }
            }
        });
    }, [execute]);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
        setCurrentUser(user);

        // Set default tab based on role
        if (user.role !== 'super_admin') {
            setActiveTab('logs');
        }

        // Then fetch data
        fetchData();
    }, [fetchData]);


    const handleDeleteAdmin = async () => {
    try {
      await api.deleteAdmin(selectedAdmin.id);
      toast.success('Administrateur supprimé');
      setShowDeleteModal(false);
      setSelectedAdmin(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleExportLogs = () => {
    const dataToExport = logs.map((log) => ({
      Date: log.date,
      Heure: log.time,
      Admin: log.admin_name,
      Action: log.action,
      Cible: log.target,
      IP: log.ip,
    }));

    exportToCSV(dataToExport, `logs-system-${new Date().toISOString().split('T')[0]}.csv`);
    toast.success('Logs exportés avec succès');
  };

        const tabs = [
            // Only show admins tab for super admins
            ...(currentUser?.role === 'super_admin'
                ? [{ id: 'admins', label: 'Administrateurs', icon: '👥' }]
                : []),
            { id: 'logs', label: 'Logs Système', icon: '📋' },
            { id: 'settings', label: 'Paramètres', icon: '⚙️' },
        ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Configuration</h1>
        <p className="text-gray-600 mt-2">
          Gestion des administrateurs, logs et paramètres système
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2
                ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'admins' && (
        <AdminsTab
          admins={admins}
          loading={loading}
          onDelete={(admin) => {
            setSelectedAdmin(admin);
            setShowDeleteModal(true);
          }}
        />
      )}

      {activeTab === 'logs' && (
        <LogsTab logs={logs} loading={loading} onExport={handleExportLogs} />
      )}

      {activeTab === 'settings' && <SettingsTab />}

      {/* Delete Admin Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAdmin}
        title="Supprimer l'administrateur"
        message={`Êtes-vous sûr de vouloir supprimer ${selectedAdmin?.firstname} ${selectedAdmin?.lastname} ? Cette action est irréversible.`}
        confirmText="Supprimer"
        confirmVariant="danger"
      />
    </div>
  );
};

// Admins Tab Component
const AdminsTab = ({ admins, loading, onDelete }) => {
    const [showFormModal, setShowFormModal] = useState(false);
    const [editingAdmin, setEditingAdmin] = useState(null);

    const handleCreateOrUpdate = async (formData) => {
        try {
            if (editingAdmin) {
                await api.updateAdmin(editingAdmin.id, formData);
                toast.success('Administrateur modifié avec succès');
            } else {
                await api.createAdmin(formData);
                toast.success('Administrateur créé avec succès');
            }
            setShowFormModal(false);
            setEditingAdmin(null);
            // Refresh the data
            window.location.reload(); // Or use a callback to refresh data
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleEdit = (admin) => {
        setEditingAdmin(admin);
        setShowFormModal(true);
    };

    const columns = [
    {
      key: 'id',
      label: 'ID',
      render: (value) => <span className="text-gray-500">#{value}</span>,
    },
    {
      key: 'name',
      label: 'Nom & Prénom',
      render: (_, row) => (
        <div>
          <p className="font-medium text-gray-900">
            {row.firstname} {row.lastname}
          </p>
          <p className="text-sm text-gray-500">{row.email}</p>
        </div>
      ),
    },
    {
      key: 'role',
      label: 'Rôle',
      render: (value) => (
        <Badge
          variant={
            value === 'super_admin'
              ? 'info'
              : value === 'admin'
              ? 'success'
              : 'default'
          }
        >
          {value === 'super_admin' ? 'Super Admin' : value === 'admin' ? 'Admin' : 'Support'}
        </Badge>
      ),
    },
    {
      key: 'created_at',
      label: 'Date de création',
      render: (value) => formatDate(value),
    },
    {
      key: 'last_login',
      label: 'Dernière connexion',
      render: (value) => formatDate(value),
    },
        {
            key: 'actions',
            label: 'Actions',
            render: (_, row) => (
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => handleEdit(row)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <PencilIcon className="w-4 h-4 text-gray-600" />
                    </button>
                    <button
                        onClick={() => onDelete(row)}
                        className="p-2 hover:bg-danger/10 rounded-lg transition-colors"
                    >
                        <TrashIcon className="w-4 h-4 text-danger" />
                    </button>
                </div>
            ),
        }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-gray-600">{admins.length} administrateur(s)</p>
          <Button
              variant="primary"
              leftIcon={<PlusIcon className="w-5 h-5" />}
              onClick={() => {
                  setEditingAdmin(null);
                  setShowFormModal(true);
              }}
          >
          Ajouter un admin
        </Button>
      </div>

      <Card padding="none">
        <Table
          columns={columns}
          data={admins}
          keyField="id"
          loading={loading}
          emptyMessage="Aucun administrateur trouvé"
        />
      </Card>

        <AdminFormModal
            isOpen={showFormModal}
            onClose={() => {
                setShowFormModal(false);
                setEditingAdmin(null);
            }}
            onSubmit={handleCreateOrUpdate}
            admin={editingAdmin}
        />
    </div>
  );
};

// Logs Tab Component
const LogsTab = ({ logs, loading, onExport }) => {
  const { filteredData, searchTerm, setSearchTerm } =
    useFilters(logs, {});

  const { currentData } = usePagination(
    filteredData,
    50
  );

  const columns = [
    {
      key: 'date',
      label: 'Date',
      render: (value, row) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{formatDate(value)}</p>
          <p className="text-xs text-gray-500">{row.time}</p>
        </div>
      ),
    },
    {
      key: 'admin_name',
      label: 'Administrateur',
      render: (value) => <span className="text-sm text-gray-900">{value}</span>,
    },
    {
      key: 'action',
      label: 'Action',
      render: (value) => <span className="text-sm font-medium text-gray-700">{value}</span>,
    },
    {
      key: 'target',
      label: 'Cible',
      render: (value) => <span className="text-sm text-gray-600">{value}</span>,
    },
    {
      key: 'ip',
      label: 'Adresse IP',
      render: (value) => (
        <span className="font-mono text-xs text-gray-500">{value}</span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Input
          placeholder="Rechercher dans les logs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
        <Button
          variant="outline"
          leftIcon={<ArrowDownTrayIcon className="w-5 h-5" />}
          onClick={onExport}
        >
          Exporter CSV
        </Button>
      </div>

      <Card padding="none">
        <Table
          columns={columns}
          data={currentData}
          keyField="id"
          loading={loading}
          emptyMessage="Aucun log trouvé"
        />
      </Card>
    </div>
  );
};

// Settings Tab Component
const SettingsTab = () => {
  const [settings, setSettings] = useState({
    alertThreshold: 3,
    emailNotifications: true,
    smtpHost: 'smtp.example.com',
    smtpPort: 587,
    smtpUser: 'noreply@zitelou.com',
  });

  const handleSave = () => {
    toast.success('Paramètres enregistrés avec succès');
  };

  return (
    <div className="space-y-6">
      <Card title="Seuils d'Alerte" subtitle="Configuration des alertes automatiques">
        <div className="space-y-4">
          <Input
            label="Nombre d'échecs de paiement avant alerte"
            type="number"
            value={settings.alertThreshold}
            onChange={(e) =>
              setSettings({ ...settings, alertThreshold: e.target.value })
            }
            helperText="L'équipe sera notifiée après ce nombre d'échecs consécutifs"
          />
        </div>
      </Card>

      <Card title="Notifications Email" subtitle="Configuration des emails">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="emailNotifications"
              checked={settings.emailNotifications}
              onChange={(e) =>
                setSettings({ ...settings, emailNotifications: e.target.checked })
              }
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <label htmlFor="emailNotifications" className="text-sm text-gray-700">
              Activer les notifications par email
            </label>
          </div>
        </div>
      </Card>

      <Card title="Configuration SMTP" subtitle="Paramètres du serveur email">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Hôte SMTP"
            value={settings.smtpHost}
            onChange={(e) => setSettings({ ...settings, smtpHost: e.target.value })}
          />

          <Input
            label="Port SMTP"
            type="number"
            value={settings.smtpPort}
            onChange={(e) => setSettings({ ...settings, smtpPort: e.target.value })}
          />

          <Input
            label="Utilisateur SMTP"
            value={settings.smtpUser}
            onChange={(e) => setSettings({ ...settings, smtpUser: e.target.value })}
            className="md:col-span-2"
          />
        </div>
      </Card>

      <div className="flex justify-end">
        <Button variant="primary" onClick={handleSave}>
          Enregistrer les paramètres
        </Button>
      </div>
    </div>
  );
};

export default Config;
